import React from 'react';
import { createRoot } from 'react-dom/client';
import {configure} from 'react-hotkeys';
import './index.css';
import { AppInsights } from "applicationinsights-js";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/nl';

// MSAL imports
import { PublicClientApplication, EventType, InteractionType  } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";

import App from './App';
import common from "./common";

import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager, provideGlobalGridOptions } from 'ag-grid-enterprise';

dayjs.extend(utc);

LicenseManager.setLicenseKey("Using_this_{AG_Grid}_Enterprise_key_{AG-096550}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Ballast_Nedam_ICT_B.V.}_is_granted_a_{Multiple_Applications}_Developer_License_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{1_March_2027}____[v3]_[01]_MTgwMzg1OTIwMDAwMA==daa169633bef4f7b7e662938f2569b94");
ModuleRegistry.registerModules([AllEnterpriseModule]);
provideGlobalGridOptions({ theme: "legacy"});

AppInsights.downloadAndSetup({ instrumentationKey: common.SETTINGS.AZURE.APPINSIGHT_INSTRUMENTATIONKEY, enableCorsCorrelation: true });

//enable correct json datetime parsing
common.JsonDateExtensions();
window.JSON.useDateParser();

export const msalInstance = new PublicClientApplication(msalConfig);

// Account selection logic is app dependent. Adjust as needed for different use cases.
const accounts = msalInstance.getAllAccounts();
if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
}

msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
        const account = event.payload.account;
        msalInstance.setActiveAccount(account);
    }
});

configure({
    /**
     * The level of logging of its own behaviour React HotKeys should perform.
     */
    logLevel: 'warn',
  
    /**
     * Default key event key maps are bound to (keydown|keypress|keyup)
     */
    defaultKeyEvent: 'keydown',
  
    /**
     * The default component type to wrap HotKey components' children in, to provide
     * the required focus and keyboard event listening for HotKeys to function
     */
    defaultComponent: 'div',
  
    /**
     * The default tabIndex value passed to the wrapping component used to contain
     * HotKey components' children. -1 skips focusing the element when tabbing through
     * the DOM, but allows focusing programmatically.
     */
    defaultTabIndex: '-1',
  
    /**
     * The HTML tags that React HotKeys should ignore key events from. This only works
     * if you are using the default ignoreEventsCondition function.
     * @type {String[]}
     */
    ignoreTags: [ 'select', 'textarea'],
  
    /**
     * Whether to ignore changes to keyMap and handlers props by default
     * (this reduces a significant amount of unnecessarily resetting
     * internal state)
     * @type {boolean}
     */
    ignoreKeymapAndHandlerChangesByDefault: true,
  
    /**
     * Whether to ignore repeated keyboard events when a key is being held down
     * @type {boolean}
     */
    ignoreRepeatedEventsWhenKeyHeldDown: true,
    
    /**
     * Whether React HotKeys should simulate keypress events for the keys that do not
     * natively emit them.
     * @type {boolean}
     */
    simulateMissingKeyPressEvents: true,
  
    /**
     * Whether to call stopPropagation() on events after they are
     * handled (preventing the event from bubbling up any further, both within
     * React Hotkeys and any other event listeners bound in React).
     *
     * This does not affect the behaviour of React Hotkeys, but rather what
     * happens to the event once React Hotkeys is done with it (whether it's
     * allowed to propagate any further through the Render tree).
     */
     stopEventPropagationAfterHandling: true,
  
    /**
     * Whether to call stopPropagation() on events after they are
     * ignored (preventing the event from bubbling up any further, both within
     * React Hotkeys and any other event listeners bound in React).
     *
     * This does not affect the behaviour of React Hotkeys, but rather what
     * happens to the event once React Hotkeys is done with it (whether it's
     * allowed to propagate any further through the Render tree).
     */
     stopEventPropagationAfterIgnoring: true,
     
     /**
     * Whether to allow combination submatches - e.g. if there is an action 
     * bound to cmd, pressing shift+cmd will *not* trigger that action when
     * allowCombinationSubmatches is false.
     */
    allowCombinationSubmatches: false,
    
    /**
     * A mapping of custom key codes to key names that you can then use in your
     * key sequences
     */
    customKeyCodes: {},
  });

const root = createRoot(document.getElementById('root'));
root.render(
    <MsalProvider instance={msalInstance}>
        <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={{
                scopes: ['https://graph.microsoft.com/User.Read']
            }}
        >
            <App />
        </MsalAuthenticationTemplate>
    </MsalProvider>
);
